import React, {MouseEvent, ReactNode, useState} from 'react';
import {get} from 'lodash';
import Typography from 'components/Typography';
import {Order} from 'types';
import Close from './close';
import {ReactComponent as MorningIcon} from 'assets/icons/morning.svg';
import {ReactComponent as DayIcon} from 'assets/icons/day.svg';
import {ReactComponent as EveningIcon} from 'assets/icons/evening.svg';
import styles from './Ticket.module.scss';
import IconList from 'components/Sidebar/MenuIcon/IconList';
import NoteIcon from 'components/Sidebar/MenuIcon/NoteIcon';
import classNames from 'classnames';
import arrow_bottom from '../../../DeliveryPlanner/components/Ticket/arrow_bottom.svg';
import {FlagIcon} from 'pages/DeliveryPlanner/icons/FlagIcon';
import CheckboxField from 'components/CheckboxField';

type Props = Order & {
  index?: number;
  arrival_time?: string;
  arrival_date?: string;
  new_leaf_order?: string;
  selected?: boolean;
  timePreference?: string;
  borough?: string;
  isAssigned: boolean;
  onChangeCheckbox: (checked: boolean, customerId: number) => void;
  onOrdersClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  handleNoteClick: () => void;
  onDelete?: () => void;
  showTime?: boolean;
  isPrint?: boolean;
  isError?: boolean;
  isCanceled?: boolean;
  globalId?: number;
  signed_at?: string;
  signed_time?: string;
  togglePopup?: (index: number, id: number) => void;
  selectionEnabled?: boolean;
};

const timePreferenceIconMap: Record<string, ReactNode> = {
  morning: <MorningIcon />,
  day: <DayIcon />,
  evening: <EveningIcon />,
};

function Ticket({
  index,
  id,
  arrival_date,
  timePreference,
  delivery_address,
  arrival_time,
  isAssigned,
  customer,
  onOrdersClick,
  handleNoteClick,
  showTime,
  isPrint = false,
  isError,
  onDelete,
  status,
  isCanceled,
  globalId,
  is_canceled,
  signed_at,
  signed_time,
  togglePopup,
  flag_reason,
  is_urgent_delivery,
  selected,
  onChangeCheckbox,
  selectionEnabled,
}: Props) {
  function naming() {
    if (typeof arrival_time === 'string') {
      const [hour, minute] = arrival_time.split(':');
      let hourInt = parseInt(hour, 10);
      let minuteInt = parseInt(minute, 10);

      let period = 'AM';
      if (hourInt >= 12) {
        period = 'PM';
      }

      if (hourInt === 0) {
        hourInt = 12;
      } else if (hourInt > 12) {
        hourInt -= 12;
      }
      return `${hourInt}:${minuteInt.toString().padStart(2, '0')} ${period}`;
    }
    return '';
  }
  const handleOrderClick = (e: MouseEvent<HTMLDivElement>) => {
    onOrdersClick(e);
  };

  const handleTooltipMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    const tooltip = e.currentTarget.querySelector(
      `.${styles.tooltip}`
    ) as HTMLElement;
    if (tooltip && flag_reason) {
      const rect = e.currentTarget.getBoundingClientRect();
      tooltip.style.top = `${rect.top - tooltip.offsetHeight - 8}px`;
      tooltip.style.right = `${window.innerWidth - rect.right}px`;
    }
  };
  const [isOpen, setIsOpen] = useState(true);
  const isHighlightedTicket = globalId === id;

  return (
    <div
      className={classNames(
        styles.root,
        isHighlightedTicket && styles.highlightedTicket,
        ((typeof delivery_address !== 'string' &&
          !delivery_address?.is_valid) ||
          isError) &&
          styles.borderColor
      )}
    >
      {index && !isPrint && (
        <div className={styles.rightElement}>
          {is_urgent_delivery && (
            <div
              className={styles.urgentDeliveryFlag}
              onMouseEnter={handleTooltipMouseEnter}
            >
              <FlagIcon width={14} height={14} />
              {flag_reason && (
                <span className={styles.tooltip}>{flag_reason}</span>
              )}
            </div>
          )}
          {!!isCanceled ? (
            <>
              <span
                onClick={() => {
                  togglePopup && togglePopup(index, id);
                }}
                className={classNames(
                  styles.index,
                  status === 'completed' && styles.completed,
                  !!is_canceled && styles.canceledIndex
                )}
              >
                {index}
              </span>

              {status !== 'completed' && !is_canceled && (
                <div onClick={onDelete} className={styles.close_icon}>
                  <Close />
                </div>
              )}
            </>
          ) : (
            <span
              onClick={() => {
                togglePopup && togglePopup(index, id);
              }}
              className={styles.canceled}
            >
              Skipped
            </span>
          )}
          <img
            onClick={() => {
              setIsOpen((pr) => !pr);
            }}
            alt="arrow bottom"
            src={arrow_bottom}
            className={isOpen ? styles.open : ''}
          />
        </div>
      )}
      {isPrint ? (
        <div className={styles.top}>
          <Typography variant="h4">STOP # {index ? index : ''}</Typography>
          {typeof arrival_time === 'string' && isAssigned && showTime && (
            <span className={styles.delivery_time}>
              {' '}
              {'-  '}
              {naming()}
            </span>
          )}
        </div>
      ) : (
        <div className={styles.topContainer}>
          <CheckboxField
            name={`checkbox-${id}`}
            className={styles.checkbox}
            checked={selected}
            onChange={() => onChangeCheckbox(!selected, customer?.id!)}
          />
          <div className={styles.top}>
            <label className={styles.label}>#{id}</label>
            {isOpen ? (
              <>
                <IconList
                  className={styles.listButton}
                  onClick={handleOrderClick}
                />
                {isAssigned && !!isCanceled && status !== 'completed' && (
                  <div>
                    <NoteIcon
                      className={styles.listButton}
                      onClick={handleNoteClick}
                    />
                  </div>
                )}
              </>
            ) : (
              <Typography className={styles.customerEllipsis} variant="p">
                {get(customer, 'user.first_name', '')}{' '}
                {get(customer, 'user.last_name', '')}
              </Typography>
            )}
          </div>
        </div>
      )}
      {isOpen && (
        <>
          {typeof customer === 'string' ? (
            <Typography className={styles.customer} variant="p">
              {customer}
            </Typography>
          ) : (
            <Typography className={styles.customer} variant="p">
              {get(customer, 'user.first_name', '')}{' '}
              {get(customer, 'user.last_name', '')}
            </Typography>
          )}

          {typeof delivery_address === 'string' ? (
            <Typography className={styles.addressCalc} variant="p">
              {delivery_address}
            </Typography>
          ) : (
            <Typography className={styles.address} variant="p">
              {delivery_address?.display_address}
            </Typography>
          )}
          <div className={styles.footer}>
            {timePreference && timePreferenceIconMap[timePreference] && (
              <span className={styles.time_preference}>
                {timePreferenceIconMap[timePreference]}
              </span>
            )}
            {typeof arrival_time === 'string' &&
              isAssigned &&
              showTime &&
              !isPrint && (
                <div className={styles.delivery_times_container}>
                  <div className={styles.delivery_time_container}>
                    <p className={styles.delivery_time}>Estimated Time</p>
                    <span className={styles.delivery_time}>
                      {arrival_date ? `${arrival_date}  ` : ''} {naming()}
                    </span>
                  </div>
                  {!!signed_at && (
                    <div className={styles.delivery_time_container}>
                      <p className={styles.delivery_time}>Delivered Time</p>
                      <span className={styles.delivery_time}>
                        {signed_at?.split('/')?.[0]?.replace('0', '') +
                          '/' +
                          signed_at?.split('/')?.[1] +
                          ' ' +
                          signed_at?.split(' ')?.[1] +
                          ' ' +
                          (signed_time?.startsWith('0')
                            ? signed_time?.slice(1)
                            : signed_time)}
                      </span>
                    </div>
                  )}
                </div>
              )}
            {!!get(delivery_address, 'unit_address', '') && isPrint && (
              <span className={styles.delivery_time}>
                Unit# {get(delivery_address, 'unit_address', '')}
              </span>
            )}
          </div>
        </>
      )}
      {!!is_canceled && <span className={styles.canceled}>Skipped</span>}
    </div>
  );
}

export default Ticket;
